import React    from "react"
import LineBase from "./line_base"

// Components
import Buttons from "./shared/buttons"

// Helpers
import { lineCalculations, formatNumber, lineNameFor } from "../helpers"

const resetAttributes = [
  "ref_product_distrib",
  "discount",
  "purchase_price",
  "vat_rate_id",
  "product_id",
  "is_option"
]

const HEADER_STYLE = "font-size: 1.2em; color: #626262"

// Markup of the descriptive line carrying the section title. The pattern lets
// us recognize a header we generated before, so it is updated instead of being
// stacked on top of itself every time the section is edited again.
const HEADER_PATTERN = /^<p><strong style="font-size: 1\.2em; color: #626262">[\s\S]*<\/strong><\/p>$/

const escapeHtml = text =>
  text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")

const subtotalPrefix = () => I18n.t("quotes.new.section_subtotal")

const headerDescriptionFor = name =>
  `<p><strong style="${HEADER_STYLE}">${escapeHtml(name)}</strong></p>`

const isSectionHeader = line =>
  !!line &&
  !line.destroyed &&
  line.type === "descriptive" &&
  HEADER_PATTERN.test((line.description || "").trim())

// A section is stored with its subtotal prefix ("Sous-total Toiture") so the
// subtotal row reads well, while the editor and the header line only ever show
// the bare name. Stripping loops so quotes already saved with stacked prefixes
// ("Sous-total Sous-total Toiture") are healed the next time they are edited.
// Only the start is trimmed: trimming the end would swallow the space being typed.
export const sectionNameOf = description => {
  const prefix = subtotalPrefix().trim()
  let   name   = (description || "").trimStart()

  if (!prefix) return name

  while (name.toLowerCase().startsWith(prefix.toLowerCase())) {
    const rest = name.slice(prefix.length)
    // Only strip a whole word: "Sous-totalisation" is a name, not a prefix.
    if (rest && !(/^\s/.test(rest))) break

    name = rest.trimStart()
  }

  return name
}

export default class Section extends LineBase {

  constructor(props) {
    super(props)
    this.onSubmit = this.onSubmit.bind(this)
  }

  onChange(e) {
    e.preventDefault()
    const { index, qline } = this.props
    const { replaceLine  } = this.props.buttonProps

    // The description is kept prefixed at all times: the quote can be saved
    // while the line is still open, and the subtotal row must read
    // "Sous-total <name>" in that case too.
    replaceLine({...qline, description: subtotalPrefix() + e.target.value}, index)
  }

  findSectionStart(quote, currentIndex) {
    for (let i = currentIndex - 1; i >= 0; i--) {
      const line = quote.qlines[i]
      if (line.destroyed) continue

      if (line.type === "section") {
        return i + 1
      }
    }
    return 0
  }

  // Header of the current section, if it was already generated — either
  // earlier in this session or in a previous edition of a saved quote.
  findSectionHeader(quote, currentIndex) {
    for (let i = this.findSectionStart(quote, currentIndex); i < currentIndex; i++) {
      if (isSectionHeader(quote.qlines[i])) return i
    }
    return -1
  }

  onSubmit() {
    const { index, qline, quote }  = this.props
    const { replaceLine, addLine } = this.props.buttonProps

    const name        = sectionNameOf(qline.description).trim() || I18n.t("quotes.new.section")
    const headerIndex = this.findSectionHeader(quote, index)

    replaceLine({...qline, description: subtotalPrefix() + name, active: false}, index)

    // The header is created once, then kept in sync with the section name.
    if (headerIndex >= 0) {
      const header = quote.qlines[headerIndex]
      replaceLine({...header, description: headerDescriptionFor(name), active: false}, headerIndex)
      return
    }

    const sectionStartIndex = this.findSectionStart(quote, index)
    addLine("descriptive", sectionStartIndex)

    const header = quote.qlines[sectionStartIndex]
    replaceLine({...header, description: headerDescriptionFor(name), active: false}, sectionStartIndex)
  }

  render() {
    const { index, qline, showCode, buttonProps } = this.props
    const calculations = lineCalculations(qline)

    return(
      <React.Fragment>
        {
          qline.active
          ? <div className = "quotes--form-qline-container">
              <div className = "text-input">
                <label required>{I18n.t("quotes.new.title")}</label>
                <input
                  className   = "form-control"
                  value       = {sectionNameOf(qline.description)}
                  placeholder = {I18n.t("quotes.new.section_prompt")}
                  onChange    = {this.onChange.bind(this)}
                  required
                />
                <input
                  type     = "hidden"
                  name     = {lineNameFor("description", index)}
                  value    = {qline.description || ""}
                  readOnly
                />
              </div>
              <div className = "quotes--form-calculations section">
                {formatNumber(calculations.total)}
              </div>
              <Buttons
                index       = {index}
                qline       = {qline}
                buttonProps = {buttonProps}
                submit      = {this.onSubmit}
              />
            </div>
          : <div
              className = "quotes--form-pointer quotes--form-table-row section"
              onClick   = {this.activate}
            >
              { showCode ? <div className = "code-column"></div> : "" }
              <div className = "description-column">
                {qline.description}
              </div>
              <div className = "price-column"/>
              <div className = "amount-column"/>
              <div className = "unit-column"/>
              <div className = "vat-column"/>
              <div className = "total-column">
                {formatNumber(calculations.total)}
              </div>
              <Buttons
                index       = {index}
                qline       = {qline}
                buttonProps = {buttonProps}
              />
              <input
                type         = "hidden"
                name         = {lineNameFor("description", index)}
                defaultValue = {qline.description}
              />
            </div>
        }
        <input
          type         = "hidden"
          name         = {lineNameFor("id", index)}
          defaultValue = {qline.id}
        />
        <input
          type         = "hidden"
          name         = {lineNameFor("order", index)}
          defaultValue = {index + 1}
        />
        <input
          type         = "hidden"
          name         = {lineNameFor("quantity", index)}
          defaultValue = {"1"}
        />
        <input
          type         = "hidden"
          name         = {lineNameFor("unit_price", index)}
          defaultValue = {qline.unit_price}
        />
        <input
          type         = "hidden"
          name         = {lineNameFor("is_section", index)}
          defaultValue = "1"
        />
        <input
          type         = "hidden"
          name         = {lineNameFor("is_subtotal", index)}
          defaultValue = "1"
        />
        
        {
          resetAttributes.map((attribute, resetIndex) =>
            <input
              key          = {resetIndex}
              type         = "hidden"
              name         = {lineNameFor(attribute, index)}
              defaultValue = ""
            />
          )
        }
      </React.Fragment>
    )
  }
}
